import React from 'react';
import { Paper, Typography, Chip, Divider, Box, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle, Mail, Users, Clock, ExternalLink, Save } from 'lucide-react';
import { useRouter } from '../../hooks/useRouter';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1),
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  taskCard: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(0.75),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    '&:last-child': { marginBottom: 0 },
  },
  taskHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  subtaskList: {
    paddingLeft: theme.spacing(2),
    margin: 0,
    '& li': {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginBottom: 2,
    },
  },
  messageCard: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(0.75),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actorChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  stats: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  savedStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: 12,
    color: theme.palette.success.main,
  },
}));

const ONE_DAY = 24 * 60 * 60 * 1000;

function formatDay(ms: number): string {
  const day = Math.round(ms / ONE_DAY);
  return `Day ${day}`;
}

interface TemplateData {
  id: string;
  title: string;
  status: string;
  tasks: Array<{
    id: string;
    title: string;
    actor?: string;
    timing?: { start?: number; end?: number };
    start?: number;
    end?: number;
    blocks?: Array<{
      id: string;
      type: string;
      requirementType?: string;
      data?: { label?: string; title?: string; name?: string };
    }>;
  }>;
  messages: {
    other?: Array<{
      id: string;
      subject: string;
      actor?: string;
      timing?: { start?: number };
    }>;
  };
  supportingActors: Record<string, { name: string; key: string; type: string; color?: string }>;
}

interface TemplatePreviewProps {
  template: TemplateData | null;
  isLoading?: boolean;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, isLoading }) => {
  const classes = useStyles();
  const { history } = useRouter();

  if (isLoading) {
    return (
      <div className={classes.empty}>
        <Typography variant="body2">Loading template preview...</Typography>
      </div>
    );
  }

  if (!template) {
    return (
      <div className={classes.empty}>
        <Typography variant="subtitle2" gutterBottom>Template Preview</Typography>
        <Typography variant="body2">
          When you create or modify a template through chat, the preview will appear here.
        </Typography>
      </div>
    );
  }

  const tasks = template.tasks ?? [];
  const messages = template.messages?.other ?? [];
  const actors = Object.values(template.supportingActors ?? {});
  const subtaskCount = tasks.reduce((sum, t) => sum + (t.blocks?.length ?? 0), 0);

  // Filter out non-subtask blocks (HEADING, TEXT, etc.)
  const CONTENT_TYPES = new Set(['HEADING', 'TEXT', 'IMAGE', 'CALLOUT', 'DIVIDER']);

  const handleOpenInEditor = () => {
    history.push(`/experiences/${template.id}/edit`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle1">{template.title}</Typography>
        <div className={classes.stats}>
          <Chip label={template.status} size="small" variant="outlined" />
          <Chip label={`${tasks.length} tasks`} size="small" variant="outlined" />
          <Chip label={`${subtaskCount} subtasks`} size="small" variant="outlined" />
          <Chip label={`${messages.length} messages`} size="small" variant="outlined" />
        </div>
      </div>

      <Divider />

      {/* Actors */}
      {actors.length > 0 && (
        <div className={classes.section} style={{ marginTop: 12 }}>
          <div className={classes.sectionTitle}>
            <Users size={14} />
            Actors
          </div>
          <div className={classes.actorChips}>
            {actors.map((actor) => (
              <Chip
                key={actor.key}
                label={actor.name}
                size="small"
                style={{
                  backgroundColor: actor.color ?? '#e0e0e0',
                  color: '#fff',
                  fontSize: 12,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tasks */}
      {tasks.length > 0 && (
        <div className={classes.section}>
          <div className={classes.sectionTitle}>
            <CheckCircle size={14} />
            Tasks
          </div>
          {tasks.map((task) => {
            const start = task.timing?.start ?? task.start ?? 0;
            const end = task.timing?.end ?? task.end ?? 0;
            const subtasks = (task.blocks ?? []).filter((b) => !CONTENT_TYPES.has(b.type));

            return (
              <div key={task.id} className={classes.taskCard}>
                <div className={classes.taskHeader}>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {task.title}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={11} />
                    <Typography variant="caption" color="textSecondary">
                      {formatDay(start)} - {formatDay(end)}
                    </Typography>
                  </div>
                </div>
                {task.actor && task.actor !== 'participant' && (
                  <Typography variant="caption" color="textSecondary">
                    Assigned to: {task.actor}
                  </Typography>
                )}
                {subtasks.length > 0 && (
                  <ul className={classes.subtaskList}>
                    {subtasks.map((st) => (
                      <li key={st.id}>
                        <Chip
                          label={st.type}
                          size="small"
                          variant="outlined"
                          style={{ height: 16, fontSize: 10, marginRight: 4 }}
                        />
                        {st.data?.label ?? st.data?.title ?? st.data?.name ?? st.type}
                        {st.requirementType === 'OPTIONAL' && (
                          <span style={{ fontStyle: 'italic', marginLeft: 4 }}>(optional)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div className={classes.section}>
          <div className={classes.sectionTitle}>
            <Mail size={14} />
            Messages
          </div>
          {messages.map((msg) => (
            <div key={msg.id} className={classes.messageCard}>
              <Mail size={14} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <Typography variant="body2" style={{ fontSize: 13 }}>
                  {msg.subject}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatDay(msg.timing?.start ?? 0)}
                  {msg.actor && msg.actor !== 'participant' ? ` · ${msg.actor}` : ''}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Action bar */}
    <div className={classes.actionBar}>
      <div className={classes.savedStatus}>
        <Save size={13} />
        <span>Saved as DRAFT</span>
      </div>
      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={handleOpenInEditor}
        endIcon={<ExternalLink size={13} />}
        style={{ textTransform: 'none', fontSize: 13 }}
      >
        Open in Editor
      </Button>
    </div>
    </div>
  );
};
